import { useState, useMemo } from "react";
import { trpc } from "../utils/trpc";
import CreateTagModal from "./CreateTagModal";
import "../styles/TagManagerModal.css";

interface Tag {
  id: number;
  name: string;
  category_id?: number;
}

interface TagSelectorProps {
  value: Tag[];
  onChange: (tags: Tag[]) => void;
  min?: number;
  max?: number;
}

function TagSelector({
  value,
  onChange,
  min = 0,
  max = Infinity,
}: TagSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateTagOpen, setIsCreateTagOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: categoriesWithTags, isLoading } =
    trpc.tags.getAllCategoriesWithTags.useQuery();

  const { data: searchedTags } = trpc.tags.searchTags.useQuery(
    { query: searchQuery },
    { enabled: searchQuery.length > 0 }
  );

  const availableTags = useMemo(() => {
    if (!categoriesWithTags) return [];

    const flattened = categoriesWithTags.flatMap((c) => c.tags ?? []);

    const unique = Array.from(
      new Map(flattened.map((t) => [t.id, t])).values()
    );

    if (searchQuery.trim()) {
      return (searchedTags ?? []).filter(
        (t) => !value.some((v) => v.id === t.id)
      );
    }

    return unique.filter((t) => !value.some((v) => v.id === t.id));
  }, [categoriesWithTags, searchedTags, searchQuery, value]);

  const addTag = (tag: Tag) => {
    if (value.length >= max) {
      setError(`You can only select up to ${max} interests.`);
      return;
    }
    onChange([...value, tag]);
    setError(null);
  };

  const removeTag = (tagId: number) => {
    if (value.length <= min) {
      setError(`You must keep at least ${min} interest(s).`);
      return;
    }
    onChange(value.filter((t) => t.id !== tagId));
  };

  const handleNewTagCreated = (tag: Tag) => {
    onChange([...value, tag]);
    setIsCreateTagOpen(false);
  };

  if (isLoading) return <div>Loading interests...</div>;

  return (
    <>
      <p className="section-subtext">Find or create interests</p>

      <input
        type="text"
        placeholder="Search or create interests..."
        className="search-input"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      <div className="tag-layout">
        {/* LEFT */}
        <section className="add-tags-section">
          <h3>Browse</h3>

          <div className="tags-container">
            {availableTags.map((tag) => (
              <button
                key={tag.id}
                className="tag addable"
                onClick={() => addTag(tag)}
                disabled={value.length >= max}
              >
                + {tag.name}
              </button>
            ))}

            {searchQuery && availableTags.length === 0 && (
              <div className="empty-state">
                <p>No matching interests</p>
                <button
                  className="create-tag-btn"
                  onClick={() => setIsCreateTagOpen(true)}
                >
                  Create "{searchQuery}"
                </button>
              </div>
            )}
          </div>
        </section>

        {/* RIGHT */}
        <section className="current-tags-section">
          <h3>Selected</h3>

          <p className="tag-limit-info">
            {value.length} / {max === Infinity ? "∞" : max}
            {min > 0 && ` (min ${min})`}
          </p>

          <div className="tags-container selected-container">
            {value.length === 0 ? (
              <p className="empty-state">No interests selected</p>
            ) : (
              value.map((tag) => (
                <div key={tag.id} className="tag-selected">
                  {tag.name}
                  <button
                    className="remove-btn"
                    onClick={() => removeTag(tag.id)}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {error && <p className="tag-error">{error}</p>}

      <CreateTagModal
        isOpen={isCreateTagOpen}
        onClose={() => setIsCreateTagOpen(false)}
        onTagCreated={handleNewTagCreated}
      />
    </>
  );
}

export default TagSelector;